import { c as _c } from "react/compiler-runtime";
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import IconAdditionalContent from '@/assets/icons/additional-content.svg';
import IconAgeTwelvePlus from '@/assets/icons/age-twelve-plus.svg';
import IconArrow from '@/assets/icons/arrow.svg';
import IconAudioDescription from '@/assets/icons/audio-description.svg';
import IconAudioOff from '@/assets/icons/audio-off.svg';
import IconAudioOn from '@/assets/icons/audio-on.svg';
import IconCheck from '@/assets/icons/check.svg';
import IconChevron from '@/assets/icons/chevron.svg';
import IconCircleMinus from '@/assets/icons/circle-minus.svg';
import IconCirclePlus from '@/assets/icons/circle-plus.svg';
import IconClose from '@/assets/icons/close.svg';
import IconClosedCaptions from '@/assets/icons/closed-captions.svg';
import IconCreditCard from '@/assets/icons/credit-card.svg';
import IconDoubleQuote from '@/assets/icons/double-quote.svg';
import IconFiveStars from '@/assets/icons/five-stars.svg';
import IconFourKQuality from '@/assets/icons/four-k-quality.svg';
import IconHeart from '@/assets/icons/heart.svg';
import IconLink from '@/assets/icons/link.svg';
import IconLock from '@/assets/icons/lock.svg';
import IconPauseReactions from '@/assets/icons/pause-reactions.svg';
import IconPaypal from '@/assets/icons/paypal.svg';
import IconPlay from '@/assets/icons/play.svg';
import IconPlayFill from '@/assets/icons/play-fill.svg';
import IconPlayFillAlt from '@/assets/icons/play-fill-alt.svg';
import IconPlayReactions from '@/assets/icons/play-reactions.svg';
import IconPlus from '@/assets/icons/plus.svg';
import IconProfile from '@/assets/icons/profile.svg';
import { cn } from '@/lib/utils';
const icons = {
  'additional-content': IconAdditionalContent,
  'age-twelve-plus': IconAgeTwelvePlus,
  arrow: IconArrow,
  'audio-description': IconAudioDescription,
  'audio-off': IconAudioOff,
  'audio-on': IconAudioOn,
  check: IconCheck,
  chevron: IconChevron,
  'circle-minus': IconCircleMinus,
  'circle-plus': IconCirclePlus,
  close: IconClose,
  'closed-captions': IconClosedCaptions,
  'credit-card': IconCreditCard,
  'double-quote': IconDoubleQuote,
  'five-stars': IconFiveStars,
  'four-k-quality': IconFourKQuality,
  heart: IconHeart,
  link: IconLink,
  lock: IconLock,
  'pause-reactions': IconPauseReactions,
  paypal: IconPaypal,
  play: IconPlay,
  'play-fill': IconPlayFill,
  'play-fill-alt': IconPlayFillAlt,
  'play-reactions': IconPlayReactions,
  plus: IconPlus,
  profile: IconProfile
} as const;
export type IconName = keyof typeof icons;
const iconVariants = cva('inline-block shrink-0', {
  variants: {
    size: {
      sm: 'size-4',
      md: 'size-5',
      default: 'size-6',
      lg: 'size-7',
      xl: 'size-8'
    },
    color: {
      default: 'text-current',
      primary: 'text-primary',
      secondary: 'text-secondary',
      muted: 'text-muted-foreground'
    },
    fillColor: {
      default: '',
      primary: 'fill-primary',
      secondary: 'fill-secondary',
      muted: 'fill-muted-foreground'
    }
  },
  defaultVariants: {
    size: 'default',
    color: 'default',
    fillColor: 'default'
  }
});
export type IconProps = {
  name: IconName;
  className?: string;
} & VariantProps<typeof iconVariants> & Omit<React.SVGProps<SVGSVGElement>, 'ref'>;
export function Icon(t0) {
  const $ = _c(16);
  let className;
  let color;
  let fillColor;
  let name;
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      name,
      className,
      size,
      color,
      fillColor,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = color;
    $[3] = fillColor;
    $[4] = name;
    $[5] = props;
    $[6] = size;
  } else {
    className = $[1];
    color = $[2];
    fillColor = $[3];
    name = $[4];
    props = $[5];
    size = $[6];
  }
  const IconComponent = icons[name];
  let t1;
  if ($[7] !== className || $[8] !== color || $[9] !== fillColor || $[10] !== size) {
    t1 = cn(iconVariants({
      size,
      color,
      fillColor
    }), className);
    $[7] = className;
    $[8] = color;
    $[9] = fillColor;
    $[10] = size;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== IconComponent || $[13] !== props || $[14] !== t1) {
    t2 = <IconComponent className={t1} aria-hidden="true" {...props} />;
    $[12] = IconComponent;
    $[13] = props;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
}